import { useEffect, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import type { BeatStatus, PageState } from '@roll1/shared';
import { ChevronDown, Columns2, FileText, Trash2 } from 'lucide-react';
import { RowMenu } from '../ui/row-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.js';
import type { TranslationKeys } from '../../i18n/types.js';
import type { Beat, JournalEntry } from './StudioRail.js';
import { BEAT_STATUS_KEY } from './beatPlan.js';
import { StudioMapPreview } from './StudioMapPreview.js';
import { AiMark, type AiMarkKind } from '../ai/AiMark.js';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Pill, pillVariants } from '../ui/pill';
import { SectionHeader } from '../ui/section-header';
import { IconButton } from '../ui/icon-button';
import { PANE_HEAD, PaneSection } from './PaneParts.js';
import type { JournalDraftStatus } from './useJournalDrafts.js';

/**
 * Панель выбранного бита в воркспейсе — недостающее звено флоу (стенд-фидбек
 * 07-24): бит без заметки давал вечный placeholder «выбери бит», потому что
 * рабочий набор собирается ТОЛЬКО из journalEntryId. Здесь бит можно назвать,
 * писать его текст подготовки прямо в обзоре (пакет 3: текст — это Lore-запись,
 * привязанная через journalEntryId), привязать другую запись/карту — после чего
 * чипы рабочего набора оживают.
 */

/**
 * The beat's prep text as the overview shows it (package 3). The text lives in
 * the linked journal entry (journalEntryId); the host decides the state.
 */
export type BeatPrepView =
  /** No text yet — «Add prep text». */
  | { readonly kind: 'none' }
  /** Being created/linked — the field appears once the server confirmed both. */
  | { readonly kind: 'pending' }
  /** The linked entry is not loaded yet. */
  | { readonly kind: 'loading' }
  | { readonly kind: 'failed'; readonly step: 'entry' | 'link'; readonly error: string }
  /** The answer was lost with the connection: recovered after the rejoin — no «Retry», it could duplicate. */
  | { readonly kind: 'unknown'; readonly step: 'entry' | 'link' }
  /**
   * The server could not say whether the entry was created (no record). It
   * may exist: the GM chooses it in «Linked entry» or creates a new one knowingly.
   */
  | { readonly kind: 'unresolved' }
  /** The linked entry was deleted: nothing is recreated without the GM. */
  | { readonly kind: 'missing' }
  | {
      readonly kind: 'entry';
      readonly entryName: string;
      /** Lore: the text is edited here; any other type is shown as is. */
      readonly lore: boolean;
      readonly typeLabel: string;
      /** Other beats whose text is this same entry. */
      readonly sharedWith: readonly string[];
    };

export interface StudioBeatPanelProps {
  readonly beat: Beat & { readonly journalEntryId?: string | null };
  readonly entries: readonly JournalEntry[];
  /** The primary map's data for the preview (package 6); null — none / not loaded. */
  readonly pageData?: PageState | null;
  readonly t: (key: keyof TranslationKeys) => string;
  readonly onRename: (label: string) => void;
  readonly onLinkEntry: (entryId: string | null) => void;
  /** Удалить бит (Батч G) — подтверждение на стороне хоста. */
  readonly onDelete?: () => void;
  /** The status of the beat chosen from its header pill. */
  readonly onSetStatus?: (status: BeatStatus) => void;
  readonly prep: BeatPrepView;
  /** kind 'entry': the text editor (Lore) or the entry's view — built by the host. */
  readonly prepBody?: ReactNode;
  /** Create a Lore entry for the text and link it (the same path as a new beat). */
  readonly onAddPrep: () => void;
  readonly onRetryPrep: () => void;
  /** Kind 'entry': the text typed here and not yet confirmed by the server. */
  readonly prepSave?: JournalDraftStatus;
  readonly onResavePrep?: () => void;
  /** Open the text's entry as a document. */
  readonly onOpenPrep: () => void;
  /** Just created by the GM: focus the name once. */
  readonly autoFocusName?: boolean;
  readonly onNameAutoFocused?: () => void;
  /** The beat's number in the main line of the plan; null — a reserve beat (P04), it has none. */
  readonly number?: number | null;
  /** P04: a reserve beat says which beat it forks from; the name opens that beat. */
  readonly reserveFrom?: { readonly name: string; readonly onOpen: () => void };
  /** «Where next» (P04) — built by the host. */
  readonly forks?: ReactNode;
  /** «Beat notes» (P03) — built by the host. */
  readonly notes?: ReactNode;
  /** «Materials» of the beat (GM Studio 3.1) — built by the host. */
  readonly materials?: ReactNode;
  /** «Maps of the beat» (GM Studio 6) — built by the host. */
  readonly maps?: ReactNode;
  /** P09b (Q-7): «Map beside» in the beat's head — the beat's map in the second pane, the beat stays open. */
  /** AU-8: «Tia added / changed» in the head of the open beat. */
  readonly aiMark?: AiMarkKind | undefined;
  readonly mapBeside?: { readonly open: boolean; readonly disabled: boolean; readonly onToggle: () => void };
}

const NONE = '__none__';

export function StudioBeatPanel({
  beat,
  entries,
  pageData,
  t,
  onRename,
  onLinkEntry,
  onDelete,
  onSetStatus,
  prep,
  prepBody,
  onAddPrep,
  onRetryPrep,
  prepSave,
  onResavePrep,
  onOpenPrep,
  autoFocusName,
  onNameAutoFocused,
  number,
  reserveFrom,
  forks,
  notes,
  materials,
  maps,
  mapBeside,
  aiMark,
}: StudioBeatPanelProps): JSX.Element {
  const currentLabel = beat.name ?? beat.sessionLabel ?? '';
  // Черновик имени: null — пользователь не редактирует, поле показывает
  // актуальное имя бита (входящие обновления применяются сразу); строка — идёт
  // ввод, входящее обновление его не перетирает. Enter / уход фокуса сохраняют
  // черновик в ЭТОТ бит (onRename хоста привязан к нему) и возвращают поле к
  // имени бита — повторный Enter/blur без нового ввода ничего не шлёт. Хост
  // монтирует панель с key = id бита, поэтому черновик не переезжает в другой бит.
  const [draft, setDraft] = useState<string | null>(null);
  const label = draft ?? currentLabel;
  const nameRef = useRef<HTMLInputElement>(null);
  const prepRef = useRef<HTMLDivElement>(null);
  // GK-4: Enter in the name (and a click into the empty prep field) leads the cursor into the prep text once the field exists.
  const [focusPrep, setFocusPrep] = useState(false);
  const prepAskedRef = useRef(false);
  useEffect((): void => {
    if (prep.kind !== 'none' && prep.kind !== 'missing') prepAskedRef.current = false;
  }, [prep.kind]);
  const askPrep = (): void => {
    setFocusPrep(true);
    if (prepAskedRef.current) return;
    prepAskedRef.current = true;
    onAddPrep();
  };
  useEffect((): void => {
    if (!focusPrep) return;
    const field = prepRef.current?.querySelector<HTMLElement>('[data-testid="beat-prep-text"]');
    if (field === null || field === undefined) return;
    field.focus();
    setFocusPrep(false);
  }, [focusPrep, prep.kind, prepBody]);
  useEffect(() => {
    if (autoFocusName !== true) return;
    nameRef.current?.focus();
    onNameAutoFocused?.();
    // Once, on mount: the panel is keyed by the beat.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commitLabel = (): void => {
    if (draft === null) return;
    setDraft(null);
    const trimmed = draft.trim();
    if (trimmed !== '' && trimmed !== currentLabel) {
      onRename(trimmed);
    }
  };

  return (
    <div className="studio-beat-panel flex min-h-full flex-1 flex-col" data-testid="studio-beat-panel">
      {/* The pane's header, one 40 px line (group T): «БИТ 2  Name  [status]  [Map beside] [⋯]» — pinned over the scroll. */}
      <div className={`${PANE_HEAD} sticky top-0 z-10 bg-panel`} data-pane-head="">
        <SectionHeader as="span" className="flex flex-none items-center gap-2">
          {number === null ? t('beat.reserve.title') : `${t('studio.beat.one')} ${number ?? beat.order}`}
          {aiMark !== undefined ? <AiMark kind={aiMark} /> : null}
        </SectionHeader>
        <input
          type="text"
          className="h-8 min-w-[8rem] flex-1 truncate rounded border border-transparent bg-transparent px-1.5 text-title font-semibold text-fg placeholder:text-dim hover:border-line focus:border-hl/50 focus:bg-surface focus:outline-none"
          aria-label={number === null ? t('beat.reserve.title') : `${t('studio.beat.one')} ${number ?? beat.order}`}
          placeholder={number === null ? t('beat.reserve.title') : `${t('studio.beat.one')} ${number ?? beat.order}`}
          data-testid="beat-panel-name"
          ref={nameRef}
          value={label}
          onChange={(e): void => setDraft(e.target.value)}
          onBlur={commitLabel}
          onKeyDown={(e): void => {
            if (e.key === 'Enter') {
              // The key's own default would put a line break into the prep field the cursor moves to.
              e.preventDefault();
              commitLabel();
              setFocusPrep(true);
            }
          }}
        />
        {/* The header of the beat carries its state (mockup): the status, and «reserve» for a reserve beat. */}
        {number === null ? (
          <Pill tone="reserve" className="flex-none" data-testid="beat-panel-reserve-pill">
            {t('beat.reserve.pill')}
          </Pill>
        ) : null}
        {onSetStatus === undefined ? (
          <Pill tone="plain" className="flex-none text-fg" data-testid="beat-panel-status" data-status={beat.status}>
            {t(BEAT_STATUS_KEY[beat.status])}
          </Pill>
        ) : (
          // The pill looks like a button, so it is one: a click chooses the status (the rail's small text cycles it).
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="inline"
                className={`${pillVariants({ tone: 'plain' })} flex-none text-fg hover:border-hl/50 hover:bg-transparent hover:text-hl`}
                data-testid="beat-panel-status"
                data-status={beat.status}
                title={t('studio.beat.chooseStatus')}
              >
                {t(BEAT_STATUS_KEY[beat.status])}
                <ChevronDown size={12} aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="beat-panel-status-menu">
              <DropdownMenuRadioGroup value={beat.status} onValueChange={(v): void => onSetStatus(v as BeatStatus)}>
                {(['draft', 'ready', 'played', 'carried'] as const).map((s) => (
                  <DropdownMenuRadioItem key={s} value={s} data-testid={`beat-panel-status-${s}`}>
                    {t(BEAT_STATUS_KEY[s])}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {mapBeside !== undefined ? (
          // An icon with its words in the hint (П3); pressed while the beat's map stands beside.
          <IconButton
            label={t(mapBeside.open ? 'studio.beat.mapHide' : 'studio.beat.mapBeside')}
            icon={<Columns2 size={14} aria-hidden="true" />}
            className={mapBeside.open ? 'bg-hl/13 text-hl hover:text-hl' : undefined}
            aria-pressed={mapBeside.open}
            data-testid="beat-map-beside-toggle"
            disabled={mapBeside.disabled}
            onClick={mapBeside.onToggle}
          />
        ) : null}
        {onDelete !== undefined ? (
          // Sidebar polish П5: no red button on the page — «Удалить бит…» is in the ⋯ of the header, the surface asks.
          <RowMenu
            size="md"
            testid="beat-panel-more"
            items={[{ label: `${t('studio.beat.delete')}…`, icon: Trash2, danger: true, testid: 'beat-panel-delete', onSelect: onDelete }]}
          />
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-3 px-3 pb-4 pt-1">

      {reserveFrom !== undefined ? (
        <p
          className="rounded-md border border-line bg-panel-2 px-2.5 py-2 text-meta text-muted-foreground"
          data-testid="beat-reserve-from"
        >
          {((): ReactNode => {
            const [before = '', after = ''] = t('beat.reserve.from').split('{beat}');
            return (
              <>
                {before}
                <Button type="button" variant="text" size="inline" onClick={reserveFrom.onOpen}>
                  {reserveFrom.name}
                </Button>
                {after}
              </>
            );
          })()}
        </p>
      ) : null}

      {/* F-7: the prep text takes the free height of the overview; when there is no room the panel scrolls (the field stays >= 160 px). */}
      <div ref={prepRef} data-testid="beat-prep" className={prep.kind === 'entry' ? 'flex flex-1 flex-col' : 'flex flex-col'}>
        <PaneSection
          title={t('studio.beatPanel.prep')}
          className={prep.kind === 'entry' ? 'flex-1' : undefined}
          actions={
            prep.kind === 'entry' ? (
              <IconButton
                label={t('studio.beatPanel.prepOpen')}
                icon={<FileText size={14} aria-hidden="true" />}
                data-testid="beat-prep-open"
                onClick={onOpenPrep}
              />
            ) : undefined
          }
        >
        {/* «Source: …» — the one place where the prep's source is chosen (mockup quests__beat has no separate block). */}
        <div className="mb-1.5 flex min-w-0 items-center gap-2 text-meta text-dim">
          <span className="flex-none">{t('studio.beatPanel.prepSource')}:</span>
          <select
            className="min-w-0 max-w-full truncate rounded border border-transparent bg-transparent px-1 py-0.5 text-meta text-fg hover:border-line focus:border-hl/50 focus:outline-none"
            data-testid="beat-panel-entry"
            aria-label={t('studio.beatPanel.note')}
            value={beat.journalEntryId ?? NONE}
            onChange={(e): void =>
              onLinkEntry(e.target.value === NONE ? null : e.target.value)
            }
          >
            <option value={NONE}>{t('studio.beatPanel.none')}</option>
            {entries.map(
              (entry: JournalEntry): JSX.Element => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ),
            )}
          </select>
        </div>
        {prep.kind === 'entry' ? (
          <div className="flex flex-1 flex-col gap-1.5">
            {prep.sharedWith.length > 0 ? (
              <p className="text-meta text-hl/90" data-testid="beat-prep-shared">
                {t('studio.beatPanel.prepShared').replace('{beats}', prep.sharedWith.join(', '))}
              </p>
            ) : null}
            {!prep.lore ? (
              <p className="text-meta text-dim" data-testid="beat-prep-other-type">
                {t('studio.beatPanel.prepOtherType').replace('{type}', prep.typeLabel)}
              </p>
            ) : null}
            {prepBody}
            {prepSave?.state === 'failed' ? (
              <div className="flex items-center gap-2 text-meta text-danger" data-testid="beat-prep-save-failed">
                <span className="min-w-0 flex-1">
                  {t('studio.beatPanel.prepSaveFailed')}
                  {prepSave.error !== undefined && prepSave.error !== '' ? `: ${prepSave.error}` : ''}
                </span>
                <Button type="button" size="sm" variant="danger" className="flex-none" data-testid="beat-prep-resave" onClick={onResavePrep}>
                  {t('studio.beatPanel.prepResave')}
                </Button>
              </div>
            ) : prepSave?.state === 'offline' ? (
              <p className="text-meta text-dim" data-testid="beat-prep-offline">
                {t('studio.beatPanel.prepOffline')}
              </p>
            ) : null}
          </div>
        ) : prep.kind === 'unknown' ? (
          <p className="text-meta text-muted-foreground" data-testid="beat-prep-unknown">
            {t('studio.beatPanel.prepUnknown')}
          </p>
        ) : prep.kind === 'unresolved' ? (
          <div className="flex flex-col items-start gap-1.5" data-testid="beat-prep-unresolved">
            <p className="text-meta text-hl/90">{t('studio.beatPanel.prepUnresolved')}</p>
            <Button type="button" size="sm" variant="outline" data-testid="beat-prep-create-new" onClick={onAddPrep}>
              {t('studio.beatPanel.prepCreateNew')}
            </Button>
          </div>
        ) : prep.kind === 'pending' || prep.kind === 'loading' ? (
          <p className="text-meta text-dim" data-testid="beat-prep-pending">
            {t(prep.kind === 'pending' ? 'studio.beatPanel.prepPending' : 'studio.beatPanel.prepLoading')}
          </p>
        ) : prep.kind === 'failed' ? (
          <div className="flex items-center gap-2 text-meta text-danger" data-testid="beat-prep-failed">
            <span className="min-w-0 flex-1">
              {t(prep.step === 'entry' ? 'studio.beatPanel.prepFailedEntry' : 'studio.beatPanel.prepFailedLink')}
              {prep.error !== '' ? `: ${prep.error}` : ''}
            </span>
            <Button type="button" size="sm" variant="danger" className="flex-none" data-testid="beat-prep-retry" onClick={onRetryPrep}>
              {t('studio.beatPanel.prepRetry')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-1.5">
            {prep.kind === 'missing' ? (
              <p className="text-meta text-dim" data-testid="beat-prep-missing">
                {t('studio.beatPanel.prepMissing')}
              </p>
            ) : null}
            {/* GK-11: the field is there at once (a reserve beat had a button); the text record is made when the cursor goes in. */}
            <Textarea
              className="min-h-40 w-full rounded-md border-line bg-panel-2/60 px-3 py-2.5 text-ui text-fg focus-visible:border-hl/50 focus-visible:ring-0"
              data-testid="beat-prep-add"
              aria-label={t('studio.beatPanel.prepAdd')}
              placeholder={t('studio.beatPanel.prepPlaceholder')}
              value=""
              readOnly
              onFocus={askPrep}
              onClick={askPrep}
            />
          </div>
        )}
        </PaneSection>
      </div>

      {forks}

      {/* Mockup order: the prep, «Where next», the quests and the materials, then the notes inside the beat, then the map. */}
      {materials}

      {notes}

      {maps !== undefined || (pageData !== undefined && pageData !== null) ? (
        <div>
          {maps}
          {pageData !== undefined && pageData !== null ? (
            <div className="mt-2" data-testid="beat-panel-primary-preview">
              <StudioMapPreview
                mapUrl={pageData.mapUrl}
                bgColor={pageData.bgColor}
                gridSize={pageData.gridSize}
                tokens={pageData.tokens}
                mapOffsetX={pageData.mapOffsetX}
                mapOffsetY={pageData.mapOffsetY}
                width={240}
                name={pageData.name}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {beat.journalEntryId === undefined || beat.journalEntryId === null ? (
        <p className="text-meta leading-relaxed text-dim">
          {t('studio.beatPanel.hint')}
        </p>
      ) : null}
      </div>
    </div>
  );
}

export default StudioBeatPanel;
